/**
 * useDeleteHandler Hook
 * Reusable delete operation handler with confirmation
 *
 * Usage:
 * const { handleDelete, isDeleting } = useDeleteHandler({
 *     deleteFn: (id) => contactService.deleteContact(id),
 *     onSuccess: (id) => setContacts(prev => prev.filter(c => c.id !== id)),
 *     confirmMessage: 'Are you sure you want to delete this contact?',
 *     successMessage: 'Contact deleted successfully',
 * });
 *
 * <button onClick={() => handleDelete(contact.id)} disabled={isDeleting}>
 *     Delete
 * </button>
 */

import { useState, useCallback } from 'react';
import { handleApiError, showSuccess } from '@/utils/errorHandler';

interface UseDeleteHandlerOptions<T = number> {
    /** Function to call for deletion */
    deleteFn: (id: T) => Promise<unknown>;
    /** Callback after successful deletion */
    onSuccess?: (id: T) => void;
    /** Confirmation message (set to false to skip confirmation) */
    confirmMessage?: string | false;
    /** Success toast message */
    successMessage?: string;
    /** Error action name for error handling */
    actionName?: string;
}

interface UseDeleteHandlerReturn<T = number> {
    handleDelete: (id: T) => Promise<boolean>;
    isDeleting: boolean;
    deletingId: T | null;
}

export function useDeleteHandler<T = number>(
    options: UseDeleteHandlerOptions<T>
): UseDeleteHandlerReturn<T> {
    const {
        deleteFn,
        onSuccess,
        confirmMessage = 'Are you sure you want to delete this item?',
        successMessage = 'Item deleted successfully',
        actionName = 'delete item',
    } = options;

    const [isDeleting, setIsDeleting] = useState(false);
    const [deletingId, setDeletingId] = useState<T | null>(null);

    const handleDelete = useCallback(async (id: T): Promise<boolean> => {
        // Show confirmation if enabled
        if (confirmMessage !== false) {
            const confirmed = window.confirm(confirmMessage);
            if (!confirmed) return false;
        }

        try {
            setIsDeleting(true);
            setDeletingId(id);

            await deleteFn(id);

            showSuccess(successMessage);
            onSuccess?.(id);

            return true;
        } catch (error) {
            handleApiError(error, actionName);
            return false;
        } finally {
            setIsDeleting(false);
            setDeletingId(null);
        }
    }, [deleteFn, onSuccess, confirmMessage, successMessage, actionName]);

    return {
        handleDelete,
        isDeleting,
        deletingId,
    };
}

/**
 * useBulkDeleteHandler Hook
 * For handling bulk delete operations
 */
interface UseBulkDeleteHandlerOptions<T = number> {
    deleteFn: (ids: T[]) => Promise<unknown>;
    onSuccess?: (ids: T[]) => void;
    confirmMessage?: string | false;
    successMessage?: string;
    actionName?: string;
}

interface UseBulkDeleteHandlerReturn<T = number> {
    handleBulkDelete: (ids: T[]) => Promise<boolean>;
    isDeleting: boolean;
}

export function useBulkDeleteHandler<T = number>(
    options: UseBulkDeleteHandlerOptions<T>
): UseBulkDeleteHandlerReturn<T> {
    const {
        deleteFn,
        onSuccess,
        confirmMessage = 'Are you sure you want to delete these items?',
        successMessage = 'Items deleted successfully',
        actionName = 'delete items',
    } = options;

    const [isDeleting, setIsDeleting] = useState(false);

    const handleBulkDelete = useCallback(async (ids: T[]): Promise<boolean> => {
        if (ids.length === 0) return false;

        if (confirmMessage !== false) {
            const confirmed = window.confirm(
                typeof confirmMessage === 'string'
                    ? confirmMessage
                    : `Are you sure you want to delete ${ids.length} items?`
            );
            if (!confirmed) return false;
        }

        try {
            setIsDeleting(true);
            await deleteFn(ids);

            showSuccess(successMessage);
            onSuccess?.(ids);

            return true;
        } catch (error) {
            handleApiError(error, actionName);
            return false;
        } finally {
            setIsDeleting(false);
        }
    }, [deleteFn, onSuccess, confirmMessage, successMessage, actionName]);

    return {
        handleBulkDelete,
        isDeleting,
    };
}

export default useDeleteHandler;
